// Login — Google OAuth (ADR-008) + an offline dev role-picker (AUTH_MODE=dev only).
// On AUTH_MODE=dev (app-dev / local / worktrees), Google is hidden: that lane has no
// GOOGLE_CLIENT_ID. Staging/prod (AUTH_MODE=real) show Google only.
import { useEffect, useState } from "react";
import { useAuth } from "../lib/auth.js";
import { useDevSnapshots } from "../hooks/useDevSnapshots.js";
import { useDevUsersForSnapshot } from "../hooks/useDevUsers.js";
import { useFeatures } from "../hooks/useFeatures.js";
import { userRoleSubtitle } from "../lib/rbacUi.js";
import { Button, Card, CardBody, CardHeader, Select, Spinner } from "../components/ui.js";
import { MotdBanner } from "./motd/MotdBanner.js";

const DEFAULT_SNAPSHOT = "demo-fictional";
const STAGING_URL = "https://staging.rrid-ucb.org";

export function Login(): JSX.Element {
  const { devLogin } = useAuth();
  const { authMode, isLoading: featuresLoading } = useFeatures();
  // Only show offline personas when health confirms AUTH_MODE=dev (never on staging/real).
  const showDevLogin = !featuresLoading && authMode === "dev";
  // Google only when not in demo-dev mode (and not while we still don't know).
  const showGoogle = !featuresLoading && authMode !== "dev";
  const [picked, setPicked] = useState("");
  const [busy, setBusy] = useState(false);
  const { data: snapshotMeta, isLoading: snapshotsLoading } = useDevSnapshots({
    enabled: showDevLogin,
  });
  const snapshots = snapshotMeta?.snapshots ?? [];
  const [dataset, setDataset] = useState<string | null>(null);

  useEffect(() => {
    if (!snapshots.length || dataset) return;
    const preferred =
      snapshotMeta?.active ??
      snapshots.find((s) => s.id === DEFAULT_SNAPSHOT)?.id ??
      snapshots[0]?.id ??
      null;
    setDataset(preferred);
  }, [snapshots, snapshotMeta?.active, dataset]);

  const { data: devUsers, isLoading: usersLoading } = useDevUsersForSnapshot(
    showDevLogin ? dataset : null,
  );
  const snapshotsResolved = !snapshotsLoading && snapshotMeta != null;
  const devAvailable = snapshotsResolved && snapshots.length > 0;
  // Don't spin forever when snapshots loaded empty (missing data-samples submodule).
  const loading =
    featuresLoading ||
    (showDevLogin &&
      (snapshotsLoading || (devAvailable && (usersLoading || !dataset))));

  return (
    <div className="flex min-h-screen flex-col">
      <MotdBanner />
      <div className="flex flex-1 items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <h1 className="text-xl font-bold text-brand">
            RR<span className="text-slate-400">\</span>ID — Portal
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {showDevLogin
              ? "Demo environment — pick a cartoon persona to continue."
              : "Sign in to continue."}
          </p>
        </CardHeader>
        <CardBody className="space-y-5">
          {showDevLogin ? (
            <div
              className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900"
              role="status"
            >
              <p className="font-medium">Google sign-in is not available here.</p>
              <p className="mt-1 text-sky-800/90">
                This org sandbox uses fictional demo data (safe for screen share). For real
                Google login, use{" "}
                <a
                  href={STAGING_URL}
                  className="font-medium underline underline-offset-2 hover:text-sky-950"
                >
                  staging.rrid-ucb.org
                </a>
                .
              </p>
            </div>
          ) : null}

          {showGoogle ? (
            <a href="/api/auth/google" className="block">
              <Button className="w-full" variant="primary">
                Sign in with Google
              </Button>
            </a>
          ) : null}

          {showDevLogin ? (
            loading ? (
              <Spinner label="Loading demo datasets…" />
            ) : devAvailable ? (
              <div className="space-y-3">
                <label className="block space-y-1">
                  <span className="text-xs font-medium text-slate-600">Dataset</span>
                  <Select
                    value={dataset ?? ""}
                    onChange={(e) => {
                      setDataset(e.target.value);
                      setPicked("");
                    }}
                    className="w-full"
                  >
                    {snapshots.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.label}
                      </option>
                    ))}
                  </Select>
                  {dataset && (
                    <p className="text-xs text-slate-500">
                      {snapshots.find((s) => s.id === dataset)?.description}
                    </p>
                  )}
                </label>

                <label className="block space-y-1">
                  <span className="text-xs font-medium text-slate-600">Persona</span>
                  <Select value={picked} onChange={(e) => setPicked(e.target.value)} className="w-full">
                    <option value="">Pick a role to impersonate…</option>
                    {(devUsers ?? []).map((u) => (
                      <option key={u.email} value={u.email}>
                        {u.name} — {userRoleSubtitle(u)}
                      </option>
                    ))}
                  </Select>
                </label>

                <Button
                  className="w-full"
                  variant="primary"
                  disabled={!picked || !dataset || busy}
                  onClick={async () => {
                    setBusy(true);
                    try {
                      await devLogin(picked, dataset ?? undefined);
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  {busy ? "Signing in…" : "Enter as this user"}
                </Button>
              </div>
            ) : snapshotsResolved && snapshots.length === 0 ? (
              <p className="text-sm text-amber-700">
                No demo snapshots found. Init the <code>data-samples</code> submodule before deploy (
                <code>git submodule update --init data-samples</code>), then rebuild.
              </p>
            ) : (
              <p className="text-sm text-slate-500">
                No demo datasets in this deployment (snapshots were not baked into the image). Redeploy
                with <code>data-samples/snapshots/*-demo-*</code>, or use{" "}
                <a href={STAGING_URL} className="underline underline-offset-2">
                  staging
                </a>{" "}
                for Google sign-in.
              </p>
            )
          ) : null}

          {featuresLoading ? <Spinner label="Checking sign-in options…" /> : null}
        </CardBody>
      </Card>
      </div>
    </div>
  );
}
